import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { platformAPI } from "../services/api";

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    platformAPI
      .analyticsOverview()
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load analytics"));
  }, []);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-gray-500">Loading…</p>;

  const trend = data.revenue_trend.map((row) => ({
    month: new Date(row.month).toLocaleDateString(undefined, { month: "short" }),
    revenue: row.revenue,
  }));

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Platform analytics</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Total shops" value={data.shops.total} />
        <Stat label="Active" value={data.shops.active} />
        <Stat label="Suspended" value={data.shops.suspended} />
        <Stat label="Trialing" value={data.shops.trialing} />
        <Stat label="MRR" value={data.mrr} />
      </div>

      <div className="bg-white rounded-xl shadow p-5 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Revenue, last 6 months (all shops)</h2>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="revenue" stroke="#0284c7" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow">
          <h2 className="font-semibold text-gray-800 px-5 py-3 border-b border-gray-100">
            Top shops by revenue
          </h2>
          <div className="divide-y divide-gray-100">
            {data.top_shops.map((shop) => (
              <Link
                key={shop.id}
                to={`/shops/${shop.id}`}
                className="flex justify-between px-5 py-3 text-sm hover:bg-gray-50"
              >
                <span>{shop.name}</span>
                <span className="font-medium">{shop.revenue}</span>
              </Link>
            ))}
            {data.top_shops.length === 0 && (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">No sales yet.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow">
          <h2 className="font-semibold text-gray-800 px-5 py-3 border-b border-gray-100">
            Shops at risk
          </h2>
          <div className="divide-y divide-gray-100">
            {data.shops_at_risk.map((shop) => (
              <Link
                key={shop.id}
                to={`/shops/${shop.id}`}
                className="flex justify-between px-5 py-3 text-sm hover:bg-gray-50"
              >
                <span>{shop.name}</span>
                <span className="text-amber-600">
                  {shop.last_sale_at
                    ? `last sale ${new Date(shop.last_sale_at).toLocaleDateString()}`
                    : "no sales yet"}
                </span>
              </Link>
            ))}
            {data.shops_at_risk.length === 0 && (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">Nobody's slipping.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
